/**
 * Product rows fetched through the Section Rendering API.
 *
 * One implementation for every row the theme fills after load — the brand
 * page's category rows, the homepage's destination modules, anything that
 * shows "products for X" where X is only known in the browser (the active
 * machine) or would need a filter the page itself doesn't carry.
 *
 *   ProductRow.fetch(url)      → rendered HTML, with retries
 *   ProductRow.paint(el, html) → count painted (0 means nothing there)
 *   ProductRow.fill(rows)      → fetch and paint a list, two at a time
 *
 * A burst of section requests gets throttled: Shopify answers 429, and the
 * theme dev server passes that on as a null section body. Both are retried
 * with a growing pause. Results are kept for ten minutes per tab, keyed by
 * URL, so browsing back and forth doesn't refetch.
 *
 * Never send Accept: application/json on these URLs — Shopify then serves the
 * URL's own JSON resource and ignores ?sections= entirely.
 */
(function () {
  'use strict';

  const CACHE_PREFIX = 'fmt:row:';
  const CACHE_TTL = 10 * 60 * 1000;
  const DELAYS = [1200, 2800, 6000];

  function cacheGet(url) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + url);
      if (!raw) return null;
      const { at, html } = JSON.parse(raw);
      return Date.now() - at < CACHE_TTL ? html : null;
    } catch { return null; }
  }

  function cacheSet(url, html) {
    try { sessionStorage.setItem(CACHE_PREFIX + url, JSON.stringify({ at: Date.now(), html })); } catch { /* blocked or full */ }
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function fetchRow(url, { signal } = {}) {
    const cached = cacheGet(url);
    if (cached !== null) return cached;

    for (let attempt = 0; ; attempt++) {
      try {
        const res = await fetch(url, { signal });
        if (res.status === 429 || res.status >= 500) throw new Error(`product row ${res.status}`);
        if (!res.ok) return '';
        const json = await res.json();
        const html = json['product-row'];
        if (html === null || html === undefined) throw new Error('product row throttled');
        cacheSet(url, html);
        return html;
      } catch (err) {
        if (err.name === 'AbortError' || attempt >= DELAYS.length) throw err;
        await sleep(DELAYS[attempt] + Math.random() * 400);
      }
    }
  }

  /** Paints a fetched row into its body. Returns the product count, 0 if empty. */
  function paint(row, html) {
    const body = row.querySelector('[data-row-body]') || row;
    const inner = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-product-row]');
    const count = Number(inner?.dataset.count || 0);
    if (!inner || count === 0 || !inner.querySelector('.product-card')) {
      body.replaceChildren();
      body.removeAttribute('aria-busy');
      return 0;
    }
    body.replaceChildren(...inner.childNodes);
    body.removeAttribute('aria-busy');
    const link = row.querySelector('[data-row-link]');
    if (link) {
      link.textContent = `View all ${count}`;
      if (inner.dataset.url) link.href = inner.dataset.url;
    }
    return count;
  }

  /**
   * Fetch and paint a list of rows, two at a time. `rows` is [{ el, url }].
   * Resolves to the count painted per row, in order; `onRow(el, count)` fires
   * as each lands so callers can hide or remove an empty one straight away.
   */
  function fill(rows, { onRow, signal } = {}) {
    const queue = rows.slice();
    const counts = new Array(rows.length).fill(0);
    let active = 0;
    return new Promise((resolve) => {
      const next = () => {
        if (!queue.length && active === 0) { resolve(counts); return; }
        while (active < 2 && queue.length) {
          const item = queue.shift();
          const index = rows.indexOf(item);
          active++;
          fetchRow(item.url, { signal })
            .then((html) => { counts[index] = paint(item.el, html); })
            .catch((err) => { if (err.name !== 'AbortError') counts[index] = paint(item.el, ''); })
            .finally(() => {
              if (onRow) onRow(item.el, counts[index]);
              active--;
              next();
            });
        }
      };
      next();
    });
  }

  window.ProductRow = { fetch: fetchRow, paint, fill };
})();
